import { Button, ImageIcon, styled } from 'canoe';
import React, { useEffect } from 'react';

import {
  ColorPicker,
  TextButton,
  TRANSPARENT,
  UNSET,
  ValueBox,
  ValueGroup,
  ValueInput,
} from '@candu-ai/craft/ui';
import { useToggle } from 'commons/hooks';
import { MediaLibrary } from 'craft/components/MediaLibrary';
import { Section } from 'craft/containers/Toolbox/components';
import { getCssResetStyleMessage, StyleHierarchy, useCss } from 'craft/hooks';
import { ToolboxComponentSchema } from 'craft/types';

import { getCssForBackgroundImg, sanitizeBackgroundImage } from './utils';

const StyledButton = styled(Button)`
  height: 32px;
`;

interface Props {
  nodeIds: string[];
  schema: ToolboxComponentSchema['Background'];
}

export const Background = ({ nodeIds, schema }: Props) => {
  const { hasBackgroundImage, hasDefaultValue, cssSelectorId } = schema;
  const { css, setCss, stylesHierarchy } = useCss(nodeIds, { cssSelectorId });
  const [isOpen, toggle] = useToggle();

  const color = css?.backgroundColor;
  const { backgroundImage } = css;
  const sanitizedImage = backgroundImage && sanitizeBackgroundImage(backgroundImage);
  const onResetBackground = () => setCss(getCssForBackgroundImg());

  const unset =
    !color &&
    !(hasDefaultValue && stylesHierarchy?.backgroundColor?.fallbackValue) &&
    (!hasBackgroundImage || !backgroundImage);

  const toggleBackground = () => {
    if (unset) {
      setCss({
        backgroundColor: hasDefaultValue
          ? stylesHierarchy?.backgroundColor?.fallbackValue || UNSET
          : TRANSPARENT,
      });
    } else {
      setCss({
        backgroundColor: hasDefaultValue
          ? stylesHierarchy?.backgroundColor?.fallbackValue
          : undefined,
        ...(hasBackgroundImage ? getCssForBackgroundImg() : {}),
      });
    }
  };

  useEffect(() => {
    // if background was previously toggled on but no values were set, reset it
    if (
      (!hasDefaultValue || !stylesHierarchy?.backgroundColor?.fallbackValue) &&
      (color === UNSET || color === TRANSPARENT) &&
      (!hasBackgroundImage || !backgroundImage)
    ) {
      toggleBackground();
    }
  }, [nodeIds]);

  useEffect(() => {
    // fix console warning for unsupported background style properties
    if (css?.['background-image']) {
      const currentValue = css['background-image'];
      setCss({ 'background-image': undefined, backgroundImage: currentValue });
    }
    if (css?.['background-size']) {
      const currentValue = css['background-size'];
      setCss({ 'background-size': undefined, backgroundSize: currentValue });
    }
    if (css?.['background-repeat']) {
      const currentValue = css['background-repeat'];
      setCss({ 'background-repeat': undefined, backgroundRepeat: currentValue });
    }
  }, [nodeIds]);

  return (
    <Section
      name="Background"
      data-cy="toolbox-section-background"
      isExpanded={!unset}
      onToggle={toggleBackground}
      toggleDisabled={color && color === stylesHierarchy?.backgroundColor?.fallbackValue}
    >
      <>
        <ValueGroup>
          <ColorPicker
            value={color || stylesHierarchy?.backgroundColor?.fallbackValue}
            onResetTooltip={getCssResetStyleMessage(StyleHierarchy.Styleguide)}
            defaultValue={
              hasDefaultValue
                ? (stylesHierarchy?.backgroundColor?.fallbackValue as string) || UNSET
                : TRANSPARENT
            }
            onChange={(backgroundColor: string) => setCss({ backgroundColor })}
          />
        </ValueGroup>

        {hasBackgroundImage && (
          <>
            <ValueGroup>
              {backgroundImage && (
                <div>
                  <ValueBox label="Image link">
                    <ValueInput isReadOnly value={sanitizedImage} />
                  </ValueBox>
                  <TextButton onClick={onResetBackground}> Reset</TextButton>
                </div>
              )}
              {!backgroundImage && (
                <StyledButton
                  appearance="outline"
                  fullWidth
                  variant="contrast"
                  size="xsmall"
                  icon={ImageIcon}
                  onClick={toggle}
                >
                  Choose background image
                </StyledButton>
              )}
            </ValueGroup>
            <MediaLibrary
              nodeIds={nodeIds}
              name="background"
              isModalOpen={isOpen}
              toggleModal={toggle}
            />
          </>
        )}
      </>
    </Section>
  );
};
